import { t } from '@/utils/i18n'
import { getConfig } from '@/config/config'
import constant from '@/constant'
import type { StringKeyValue } from '@/types/interfaces'
import type { CloudProduct } from '@/types/product'
import * as CryptoJS from 'crypto-js'
import dayjs from 'dayjs'
import localStorage from '@/utils/localStorage'
import VConsole from 'vconsole'
import kpVconsolePlugin from '@/plugins/vconsole-plugin'

const { setLocalStorage, getLocalStorage } = localStorage
const {
  product: { REQUEST_EXPIRED },
} = constant
const config = getConfig()

/*
 * 将Promisr的结果和错误，转化为一个数组返回。
 * @params {Promise}
 * @return {array} [err, res]
 */
const to = (promise: Promise<any>) =>
  promise.then((res) => [null, res]).catch((err) => [err, null])

/**
 *  延迟一定时间，单位毫秒。
 */
const wait = async (time: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, time)
  })

/**
 * @param 获取n位的数字随机数
 */
function randomn(n: number): string {
  if (n > 21) return ''

  const num = (Math.random() + 1) * 10 ** (n - 1)
  return String(parseInt(String(num), 10))
}

/**
 * 获取url中的参数
 *
 * @param {*} url
 * @returns {Object}
 */
// function getUrlParams(url: string): object {
//   const [, query] = url.split('?')

//   if (!query) {
//     return {}
//   }

//   interface Params {
//     [key: string]: any
//   }

//   const params: Params = {}

//   query.split('&').forEach((item) => {
//     const [key = '', value = ''] = item.split('=')
//     params[key] = value
//   })

//   return params
// }

/**
 * [px2rem 像素转vw单位]
 * @param px [需要转换的像素]
 */
function px2vw(px: number) {
  const vm = (px / config.designWidth) * 100
  return vm.toFixed(6) + 'vw'
}

function px2rem(px: number) {
  const rem = config.designWidth / 10
  return px / rem + 'rem'
}

/**
 * 获取转义后的正则表达式字符串，用于new RegExp()中
 *
 * @param {*} str
 * @returns
 */
function getEscapeRegStr(str: string) {
  return str.replace(/[()$*+.?^{}[]|]/g, function replace(match) {
    return `\\${match}`
  })
}

/**
 * 构造出错提示语
 *
 * @param {*} err
 * @param {*} msg
 * @returns
 */
function buildErrorMsg(err: any, msg?: any) {
  if (!err) {
    return t('出错了，请检查网络连接或重试')
  }

  if (typeof err === 'string') {
    return t('{{err}}，请检查网络连接或重试', { err: err })
  }

  if (msg) {
    return (
      err.msg ||
      err.errMsg ||
      err.message ||
      err.err_msg ||
      t('{{msg}}，请检查网络连接或重试', { msg: msg })
    )
  }

  return err.msg || err.errMsg || err.message || err.err_msg || t('出错了~')
}

/**
 * 获取日期对象里面月的天数
 *
 * @param {*} date
 * @returns
 */
function getMonthLength(date: Date) {
  const d = new Date(date)
  const month = d.getMonth() + 1

  // 将日期设置为下月一号
  d.setMonth(month)
  d.setDate(1)

  // 获取本月最后一天
  d.setDate(d.getDate() - 1)

  return d.getDate()
}

/**
 * 获取最后一天
 * @param year
 * @param month
 * @param separator
 * @returns
 */
function getLastDay(year: number, month: number, separator: string) {
  const lastDate = new Date(year, month, 0).getDate()
  const monthStr = month < 10 ? 0 + month : month
  return [year, monthStr, lastDate].join(separator)
}

/**
 * 获取今天的时间戳范围
 *
 * @returns
 */
function getTodayRange() {
  const now = new Date()
  const year = now.getFullYear()
  const month = now.getMonth() + 1
  const date = now.getDate()
  const startTime = +new Date(`${year}/${month}/${date} 00:00:00`)
  const endTime = +new Date(`${year}/${month}/${date} 23:59:59`)

  return [startTime, endTime]
}

/**
 * 获取昨天的时间戳范围
 *
 * @returns
 */
function getYesterdayRange() {
  const now = new Date()

  now.setDate(now.getDate() - 1)

  const year = now.getFullYear()
  const month = now.getMonth() + 1
  const date = now.getDate()
  const startTime = +new Date(`${year}/${month}/${date} 00:00:00`)
  const endTime = +new Date(`${year}/${month}/${date} 23:59:59`)

  return [startTime, endTime]
}

/**
 * 获取前天的时间戳范围
 *
 * @returns
 */
function getBeforeYesterdayRange() {
  const now = new Date()

  now.setDate(now.getDate() - 2)

  const year = now.getFullYear()
  const month = now.getMonth() + 1
  const date = now.getDate()
  const startTime = +new Date(`${year}/${month}/${date} 00:00:00`)
  const endTime = +new Date(`${year}/${month}/${date} 23:59:59`)

  return [startTime, endTime]
}

/**
 * 获取本月的时间戳范围
 *
 * @returns
 */
function getThisMonthRange() {
  const now = new Date()
  const year = now.getFullYear()
  const month = now.getMonth() + 1
  const day = now.getDate()
  const thisMonthStartTime = +new Date(`${year}/${month}/1 00:00:00`)
  const thisMonthEndTime = +new Date(`${year}/${month}/${day} 23:59:59`)

  return [thisMonthStartTime, thisMonthEndTime]
}

/**
 * 获取今天的时间戳范围
 *
 * @returns
 */
function getThisYearRange() {
  const now = new Date()
  const year = now.getFullYear()
  const month = now.getMonth() + 1
  const day = now.getDate()
  const thisYearStartTime = +new Date(`${year}/1/1 00:00:00`)
  const thisYearEndTime = +new Date(`${year}/${month}/${day} 23:59:59`)

  return [thisYearStartTime, thisYearEndTime]
}

/**
 * 是否为今天
 *
 * @param {*} startTime
 * @param {*} endTime
 * @returns
 */
function isToday(startTime: number, endTime: number) {
  const [todayStartTime, todayEndTime] = getTodayRange()

  if (startTime === todayStartTime && endTime === todayEndTime) {
    return true
  }

  return false
}

/**
 * 是否为本月
 *
 * @param {*} startTime
 * @param {*} endTime
 * @returns
 */
function isThisMonth(startTime: number, endTime: number) {
  const [thisMonthStartTime, thisMonthEndTime] = getThisMonthRange()

  if (startTime === thisMonthStartTime && endTime === thisMonthEndTime) {
    return true
  }

  return false
}

/**
 * 是否为今年
 *
 * @param {*} startTime
 * @param {*} endTime
 * @returns
 */
function isThisYear(startTime: number, endTime: number) {
  const [thisYearStartTime, thisYearEndTime] = getThisYearRange()

  if (startTime === thisYearStartTime && endTime === thisYearEndTime) {
    return true
  }

  return false
}

/**
 * 手机号码格式化
 *
 * @param {*} phone
 * @param {string} [delimiter=' ']
 * @returns
 */
function phoneFormatter(phone: string, delimiter = ' ') {
  if (!/\d{11}/.test(phone)) {
    return phone
  }

  return phone.toString().replace(/(?=(\B)(\d{4})+$)/g, delimiter)
}

/**
 *
 *
 * @param {*} val
 * @param {*} precision
 * @returns
 */
function formatByPrecision(
  val: string | number,
  precision: number,
  isDecimals = false,
) {
  if (precision < 0) {
    return val
  }

  const number = Number(val)

  if (number !== 0 && !number) {
    return 0
  }

  const tmp = number.toFixed(precision).split('.')
  tmp[0] = tmp[0].replace(/\B(?=(?:\d{3})+(?!\d))/g, ',')

  if (tmp[1]) {
    if (!isDecimals) {
      tmp[1] = tmp[1].replace(/0+$/, '')
    }
  }

  if (tmp[1]) {
    return tmp.join('.')
  }

  return tmp[0]
}

/**
 * 检查手机号是否合法
 *
 * @param {*} phone
 * @returns
 */
function checkPhone(phone: string) {
  return /^1[0-9]{10}$/.test(phone)
}

/**
 * 函数防抖
 *
 * @param {function} fn 要执行的函数
 * @param {number} wait 等待时间
 * @param {boolean} immediate 是否立即执行，immediate为true时：立即执行，停止触发n秒后，才可以重新触发执行。immediate为false时：停止触发n秒后才执行
 * @returns {function}
 */
// eslint-disable-next-line @typescript-eslint/ban-types
function debounce(fn: Function, ...args1: any[]): (...args: any[]) => void {
  let [wait, immediate] = args1

  if (wait === void 0) {
    wait = 500
    immediate = false
  }

  if (immediate === void 0) {
    immediate = false
  }

  let timer: any = null

  return function debounceFn(...args2: any[]) {
    if (timer !== null) {
      clearTimeout(timer)
    }

    if (immediate) {
      const callNow = timer === null
      timer = setTimeout(() => {
        timer = null
      }, wait)

      if (callNow) {
        fn.apply(fn, args2)
      }
    } else {
      timer = setTimeout(() => {
        fn.apply(fn, args2)
        timer = null
      }, wait)
    }
  }
}

/**
 * 函数节流
 *
 * 函数在一段时间内多次触发只会执行第一次，在这段时间结束前，不管触发多少次也不会执行函数。
 *
 * @param {*} fn
 * @param {number} [gapTime=500]
 * @returns
 */
// eslint-disable-next-line @typescript-eslint/ban-types
function throttle(
  fn: (...args: any[]) => any,
  gapTime: number = 500,
): (...args: any[]) => void {
  let lastTime: number = 0

  // 返回新的函数
  return function throttleFn(...args: any[]) {
    const nowTime: number = Date.now()

    // 函数执行条件
    if (!lastTime || nowTime - lastTime > gapTime) {
      fn.apply(fn, args) // 将this和参数传给原函数
      lastTime = nowTime
    }
  }
}

/*
 * [isTherePhone 判断字符串中是否包含手机号；格式为1[0-9] 或 1xx-xxxx-xxxx]
 * @param  {[type]}  str [description]
 * @return {String}     [返回符合规则的手机号]
 */
function isTherePhone(str: string) {
  const list = str.match(/1[0-9-]{1,}/g)
  const reg = /(^1[0-9]{10}$)|(^1[0-9]{2}-[0-9]{4}-[0-9]{4}$)/

  if (list) {
    for (let i = 0; i < list.length; i++) {
      if (reg.test(list[i])) {
        return list[i]
      }
    }
  }
}

/**
 * 是否为企业微信环境
 *
 * @returns {boolean}
 */
function isWxwork(): boolean {
  return /wxwork/i.test(window.navigator.userAgent)
}

/**
 * 是否非生产环境
 *
 * @returns {boolean}
 */
function isNotProd(): boolean {
  return NODE_ENV !== 'production'
}

/**
 * 是否为dev环境
 *
 * @returns {boolean}
 */
function isDev(): boolean {
  return NODE_ENV === 'development'
}

/**
 * 生成uuid
 *
 * @param {number} [method=1]
 * @param {*} args
 * @returns
 */
function getUuid(upperCase = false) {
  const uuidArr = []
  const chars =
    '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz'.split('')

  let r

  uuidArr[14] = '4'
  uuidArr[8] = '-'
  uuidArr[13] = '-'
  uuidArr[18] = '-'
  uuidArr[23] = '-'

  for (let i = 0; i < 36; i++) {
    if (!uuidArr[i]) {
      r = 0 | (Math.random() * 16)
      uuidArr[i] = chars[i === 19 ? (r & 0x3) | 0x8 : r]
    }
  }

  const uuid = uuidArr.join('')
  return upperCase ? uuid.toUpperCase() : uuid.toLowerCase()
}

// 判断是否为手机环境，有ontouchstart方法即为手机环境
function isMobile() {
  return 'ontouchstart' in document.documentElement
}

// 判断是否为 windows 环境
function isWindows() {
  return /windows|win32/i.test(navigator.userAgent)
}

/**
 * 为url添加参数，如果参数已存在，则覆盖其值，返回修改后的url
 * @param {String} url - 要添加参数的url
 * @param {Object} params - 参数对象
 * @returns {String} - 修改后的url
 */
function urlAddParams(url: string, params: any) {
  const [basicUrl, originParamsStr = ''] = url.split('?')
  const originParamsArr = originParamsStr.split('&')
  const originParamsObj: StringKeyValue = {}

  originParamsArr.forEach((item: any) => {
    const [key, value = ''] = item.split('=')

    if (key) {
      originParamsObj[key as keyof typeof originParamsObj] = value
    }
  })

  Object.keys(params).forEach((key: any) => {
    originParamsObj[key] = params[key]
  })

  const newParamsStr = Object.keys(cleanEmptyProps(originParamsObj))
    .map((key: any) => {
      return `${key}=${originParamsObj[key]}`
    })
    .join('&')

  return `${basicUrl}?${newParamsStr}`
}
/**
 * 清除对象的空属性(只检查一级属性)
 *
 * @param {*} obj
 * @returns
 */
function cleanEmptyProps(obj: any) {
  const newObj = { ...obj }

  Object.keys(newObj).forEach((key: any) => {
    const value = newObj[key]

    if (
      isNull(value) ||
      isUndefined(value) ||
      isEmptyObject(value) ||
      isEmptyArray(value)
    ) {
      delete newObj[key]
    }
  })

  return newObj
}

/**
 * 是否为Null类型
 *
 * @param {*} obj
 * @returns
 */
function isNull(obj: any) {
  return Object.prototype.toString.call(obj) === '[object Null]'
}

/**
 * 是否为Undefined类型
 *
 * @param {*} obj
 * @returns
 */
function isUndefined(obj: any) {
  return Object.prototype.toString.call(obj) === '[object Undefined]'
}

/**
 * 数据是否为空
 * @param obj
 * @returns
 */
function isExist(obj: any) {
  return !isNull(obj) && !isUndefined(obj)
}

/**
 * 是否为空对象
 *
 * @param {*} obj
 * @returns
 */
function isEmptyObject(obj: any) {
  return isObject(obj) && Object.keys(obj).length === 0
}

/**
 * 是否为空数组
 *
 * @param {*} arr
 * @returns
 */
function isEmptyArray(arr: any) {
  return isArray(arr) && arr.length === 0
}

/**
 * 是否为Array类型
 *
 * @param {*} obj
 * @returns
 */
function isArray(obj: any) {
  return Object.prototype.toString.call(obj) === '[object Array]'
}

/**
 * 是否为Object类型
 *
 * @param {*} obj
 * @returns
 */
function isObject(obj: any) {
  return Object.prototype.toString.call(obj) === '[object Object]'
}

/**
 * 关闭页面
 *
 */
function closeWindow() {
  if (window.WeixinJSBridge) {
    window.WeixinJSBridge.call('closeWindow')
  }

  return true
}
/**
 * 根据提供的键检索URL参数。
 * @param {string} key - 要检索参数值的键。如果未提供，则返回整个参数对象。
 * @return {Record<string, string> | string | null} - 指定键的值，如果未提供键，则返回整个参数对象。
 */
function getUrlParams(key?: string): Record<string, string> | string | null {
  const params: Record<string, string> = {}

  if (window.URLSearchParams && window.location.hash) {
    const hash = window.location.hash
    const queryString = hash.substring(hash.indexOf('?') + 1)
    const searchParams = new URLSearchParams(queryString)

    // 如果未传递 key，则返回全部参数对象
    if (!key) {
      for (const [paramKey, value] of searchParams.entries()) {
        params[paramKey] = value
      }
      return params
    }

    // 获取特定 key 的值
    return searchParams.get(key)
  } else {
    const queryString = window.location.search.substr(1)
    const paramPairs = queryString.split('&')

    for (const pair of paramPairs) {
      const [paramKey, value] = pair.split('=')
      params[decodeURIComponent(paramKey)] = decodeURIComponent(value || '')
    }

    // 如果未传递 key，则返回全部参数对象
    if (!key) {
      return params
    }

    // 获取特定 key 的值
    return params[key]
  }
}

/**
 * 小数转百分比
 * @param value
 * @returns
 */
function toPercentage(value: number) {
  return `${value * 100}%`
}

/**
 * 兼容地获取浏览器宽高
 * @returns
 */
function getWindowSize(): { windowWidth: number; windowHeight: number } {
  const windowWidth =
    window.innerWidth ||
    document.documentElement.clientWidth ||
    document.body.clientWidth
  const windowHeight =
    window.innerHeight ||
    document.documentElement.clientHeight ||
    document.body.clientHeight
  return { windowWidth, windowHeight }
}

function getProductAttrs(product: CloudProduct, attrList: any[]) {
  const validAttrs: string[] = []
  attrList.map(function (item) {
    // cloudShowStatus 显示开关 0关闭 1开启
    if (item.cloudShowStatus == 1 && !!product[item.attrKey]) {
      validAttrs.push(product[item.attrKey])
    }
  })
  const strAttrs = validAttrs.join(' | ')
  return strAttrs.trim()
}

/**
 *
 *
 * @param {*} num
 * @returns
 */
function kilobitFormat(num: number) {
  return `${num}`.replace(/\d+/, function getInteger(n) {
    // 先提取整数部分
    return n.replace(/(\d)(?=(\d{3})+$)/g, function addSeparator($1) {
      // 对整数部分添加分隔符
      return `${$1},`
    })
  })
}

/**
 * 获取类型
 * @param {*} val
 * @returns
 */
function getType(val: any) {
  return Object.prototype.toString.call(val).slice(8, -1).toLowerCase()
}

/**
 * 深拷贝
 * @param obj
 * @returns
 */
function cloneDeep<T>(obj: T): T {
  if (typeof obj !== 'object' || obj === null) {
    return obj // 非对象或 null，直接返回
  }

  if (Array.isArray(obj)) {
    const copyArr = obj.map((item) => cloneDeep(item)) // 复制数组
    return copyArr as any as T
  }

  const copyObj = {} as T

  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      // 忽略原型链上的属性
      copyObj[key] = cloneDeep(obj[key]) // 递归复制对象中的每个属性
    }
  }

  return copyObj
}

function formatMD5(str: string) {
  const encrypted = CryptoJS.MD5(str).toString()
  return encrypted
}

/**
 * 比对2个数组是否相等
 * @param list1
 * @param list2
 * @returns
 */
function diffTwoList(list1: any[], list2: any[]) {
  const _list1 = [...list1].sort()
  const _list2 = [...list2].sort()

  return _list1.toString() === _list2.toString()
}

/**
 * 将callback形式的api转换为以Promise风格调用的api
 *
 * @param {function} fn
 * @returns
 */
function promisify(fn: (args: any) => void) {
  // 如果fn不是函数则直接返回fn
  if (typeof fn !== 'function') {
    return fn
  }

  // 返回新的函数
  return function newFn(options: any = {}) {
    // 如果options包含success和fail属性，直接执行api并将结果返回
    if (options.success && options.fail) {
      return fn(options)
    }

    return new Promise((resolve, reject) => {
      options.success = function success(res: any) {
        resolve(res)
      }

      options.fail = function fail(err: any) {
        reject(err)
      }

      fn(options)
    })
  }
}

/**
 * 解决请求竞态的问题，只有最后一次请求返回请求结果
 * @param {*} fn
 * @returns
 */
function onlyLastRequest(fn: (args: any) => Promise<any>) {
  let requestTime = 0

  return function requestFn(...args: any[]) {
    return new Promise((resolve, reject) => {
      const currentRequestTime = Date.now()
      requestTime = currentRequestTime

      fn(args)
        .then((res) => {
          if (currentRequestTime !== requestTime) {
            reject(REQUEST_EXPIRED)
            return
          }

          resolve(res)
        })
        .catch(reject)
    })
  }
}

// 首字母大写
function capitalizeFirstLetter(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

/**
 * 获取今天日期范围
 * @returns
 */
function getToday() {
  const startDate = dayjs().startOf('date').toDate()
  const endDate = dayjs().endOf('date').toDate()
  return {
    startDate,
    endDate,
  }
}

/**
 * 获取这个月日期范围
 * @returns
 */
function getThisMonth() {
  const startDate = dayjs().startOf('month').toDate()
  const endDate = dayjs().endOf('month').toDate()
  return {
    startDate,
    endDate,
  }
}

/**
 * 获取这个季度日期范围
 * @returns
 */
function getThisQuarter() {
  const startDate = dayjs().startOf('quarter').toDate()
  const endDate = dayjs().endOf('quarter').toDate()
  return {
    startDate,
    endDate,
  }
}

/**
 * 获取今年日期范围
 * @returns
 */
function getThisYear() {
  const startDate = dayjs().startOf('year').toDate()
  const endDate = dayjs().endOf('year').toDate()
  return {
    startDate,
    endDate,
  }
}

/**
 * 返回最近1年
 * @returns
 */
function getLatestYear() {
  const startDate = dayjs().subtract(1, 'year').startOf('date').toDate()
  const endDate = dayjs().endOf('date').toDate()
  return {
    startDate,
    endDate,
  }
}

/**
 * 根据开始时间和结束时间，计算出日期简称
 * @param times
 * @returns
 */
function computedDateTag(times: { startTime: number; endTime: number }) {
  interface Dates {
    startDate: Date
    endDate: Date
  }
  const { startTime, endTime } = times
  const dates = {
    startDate: new Date(startTime),
    endDate: new Date(endTime),
  }

  function formatDate(date: Date) {
    return dayjs(date).format('YYYY-MM-DD')
  }

  function patchDates(dates1: Dates, dates2: Dates) {
    if (!dates1 || !dates2) return false
    const { startDate: startDate1, endDate: endDate1 } = dates1
    const { startDate: startDate2, endDate: endDate2 } = dates2
    return (
      formatDate(startDate1) === formatDate(startDate2) &&
      formatDate(endDate1) === formatDate(endDate2)
    )
  }

  if (patchDates(dates, getToday())) {
    return 'today'
  } else if (patchDates(dates, getThisMonth())) {
    return 'thisMonth'
  } else if (patchDates(dates, getThisQuarter())) {
    return 'thisQuarter'
  } else if (patchDates(dates, getThisYear())) {
    return 'thisYear'
  }
  return null
}

/**
 * 获取url中的查询参数
 * @returns
 */
function getQuerySearch() {
  const queryString = window.location?.search || ''
  return queryString
}

/**
 * 获取并且记录url中的查询参数
 * @returns
 */
function getUrlAllParams() {
  let queryString = getQuerySearch()
  if (!queryString) return
  queryString = queryString.substr(1)
  const paramPairs = queryString.split('&')

  for (const pair of paramPairs) {
    const [paramKey, value] = pair.split('=')
    setLocalStorage(paramKey, value)
  }
}

// 是否禁用 body 滚动
function isDisableBodyScroll(isDisable: boolean) {
  if (isDisable) {
    document.body.classList.add('overflow-hidden')
  } else {
    document.body.classList.remove('overflow-hidden')
  }
}

/**
 * 获取email的href链接
 * @param email
 * @param options
 * @returns
 */
function getEmailHref(email: string, options?: any) {
  let emailData = 'mailto:' + email
  if (options && typeof options === 'object') {
    emailData += '?'
    for (const key in options) {
      emailData += `${key}=${options[key]}&`
    }
    emailData = emailData.substring(0, emailData.length - 1)
  }
  return emailData
}

function isNumber(value: any) {
  return typeof value === 'number' && !isNaN(value)
}

// 拼接图片地址
function concatenateImageUrl(imgUrl: string) {
  const ossRoot = getLocalStorage('ossRoot') || config.ossRoot

  if (imgUrl.startsWith('/')) {
    return ossRoot + imgUrl.slice(1)
  }

  return ossRoot + imgUrl
}

/**
 * 复制文本到剪贴板
 *
 * @param {string} text
 */
function copyToClipboard(text: string) {
  const textArea = document.createElement('textarea')

  textArea.value = text
  document.body.appendChild(textArea)
  textArea.select()
  document.execCommand('copy')
  document.body.removeChild(textArea)
}

/**
 * 下载文件
 * @param url 文件的URL地址
 * @param fileName 下载文件的名称
 */
function downloadFile(url: string, fileName: string) {
  const a = document.createElement('a')

  a.style.display = 'none'
  a.href = url
  a.download = fileName
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}

/**
 * 从给定的图片URL获取Base64编码的图片数据。
 * @param imgUrl 图片的URL。
 * @returns 返回一个Promise，当图片加载完成时，将解析为Base64编码的图片数据。
 */
function getImageBase64(imgUrl: string) {
  return new Promise((resolve, reject) => {
    const imgElement = document.createElement('img')

    imgElement.setAttribute('crossOrigin', 'anonymous')

    imgElement.onload = function () {
      // 创建一个 canvas 元素
      const canvas = document.createElement('canvas')

      canvas.width = imgElement.width
      canvas.height = imgElement.height

      // 获取 canvas 的 2D 上下文
      const ctx = canvas.getContext('2d') as CanvasRenderingContext2D

      // 将图片绘制到 canvas 上
      ctx.drawImage(imgElement, 0, 0)

      // 获取 Base64 编码的图片数据
      const dataURL = canvas.toDataURL('image/png')

      resolve(dataURL)
    }

    imgElement.onerror = function (err) {
      reject(err)
    }

    imgElement.src = imgUrl
  })
}

/**
 * 是否在webview中
 * @returns
 */
function isWebview() {
  return window.webview || /webview/i.test(navigator.userAgent)
}

/**
 * 初始化vconsole
 */
export function initVConsole() {
  const kpVconsole = new VConsole({
    pluginOrder: ['kp'],
  })

  kpVconsole.addPlugin(kpVconsolePlugin)
}

/**
 * 在一定的时间内触发数次后执行
 * @param {*} callback
 * @param {*} count
 * @param {*} time
 * @returns
 */
export function onClickSevalTimes(callback = () => {}, count = 1, time = 2000) {
  let clickTimes = 0
  let timer: any = null

  return function () {
    console.log(1111)
    if (!timer) {
      timer = setTimeout(() => {
        if (clickTimes >= count) {
          callback()
        }
        clickTimes = 0
        clearTimeout(timer)
        timer = null
      }, time)
    }
    clickTimes++
  }
}

export default {
  to,
  wait,
  randomn,
  getUrlParams,
  getUrlAllParams,
  px2vw,
  px2rem,
  getEscapeRegStr,
  buildErrorMsg,
  getMonthLength,
  getTodayRange,
  getYesterdayRange,
  getBeforeYesterdayRange,
  getThisMonthRange,
  getThisYearRange,
  isToday,
  isThisMonth,
  isThisYear,
  getLastDay,
  phoneFormatter,
  formatByPrecision,
  checkPhone,
  debounce,
  throttle,
  isTherePhone,
  isWxwork,
  isNotProd,
  isDev,
  getUuid,
  isMobile,
  urlAddParams,
  cleanEmptyProps,
  isWindows,
  closeWindow,
  toPercentage,
  getWindowSize,
  getProductAttrs,
  kilobitFormat,
  getType,
  cloneDeep,
  isNull,
  isUndefined,
  formatMD5,
  isArray,
  diffTwoList,
  promisify,
  isExist,
  onlyLastRequest,
  capitalizeFirstLetter,
  getToday,
  getThisMonth,
  getThisQuarter,
  getThisYear,
  getLatestYear,
  computedDateTag,
  isDisableBodyScroll,
  getEmailHref,
  isNumber,
  concatenateImageUrl,
  getQuerySearch,
  copyToClipboard,
  downloadFile,
  getImageBase64,
  isWebview,
  isEmptyObject,
  initVConsole,
  onClickSevalTimes,
}
